import React, { useState } from 'react';
import { Link, Route, useLocation } from 'react-router-dom';

import { useSelector } from 'react-redux';

import styles from './Header.module.scss';
import { Routes } from '../../../routes';
import { IState } from '../../../store/IState';
import { UserResponseModel } from '../../../models/users/UserResponseModel';
import { UserSecurityRoles } from '../../../shared/userSecurityRoles';
import { getCurrentEnvironment } from '../../../shared/constants';

interface ILinkGroup {
	name: string;
	isAllowed: boolean;
	links: ILink[];
	link?: string;
}

interface ILink {
	header: string;
	link: string;
}

const Header = (): JSX.Element => {
	const [activeGroup, setActiveGroup] = useState<string>('');
	const [, setActiveChildLink] = useState<string>('');

	const location = useLocation();

	const currentEnvironment = getCurrentEnvironment();

	// We signalize users if current environment is not prod
	const showEnvironmentWarning = currentEnvironment !== 'prod';

	const user: UserResponseModel = useSelector(
		(state: IState) => state.UserReducer.userDetails
	);

	const hasAccessToAdminSection =
		user &&
		(user.securityRoleCode === UserSecurityRoles.MarketShareAdminRoleCode ||
			user.securityRoleCode ===
				UserSecurityRoles.ServiceShareAdminRoleCode);

	const hasAccessToPeriodStatusSection =
		user &&
		(user.securityRoleCode === UserSecurityRoles.MarketShareAdminRoleCode ||
			user.securityRoleCode ===
				UserSecurityRoles.ServiceShareAdminRoleCode);

	const hasAccessToServiceShareInputSection =
		user &&
		(user.securityRoleCode === UserSecurityRoles.MarketShareAdminRoleCode ||
			user.securityRoleCode ===
				UserSecurityRoles.ServiceShareAdminRoleCode ||
			user.securityRoleCode ===
				UserSecurityRoles.ServiceShareUserRoleCode);

	const hasAccessToManualInputSection =
		user &&
		(user.securityRoleCode === UserSecurityRoles.MarketShareAdminRoleCode ||
			user.securityRoleCode ===
				UserSecurityRoles.ServiceShareAdminRoleCode ||
			user.securityRoleCode ===
				UserSecurityRoles.MarketShareUserRoleCode);

	const linkGroups: ILinkGroup[] = [
		{
			name: 'Market Share input',
			isAllowed: hasAccessToManualInputSection,
			links: [],
			link: Routes.manualInput.getPath()
		},
		//{
		//	name: 'Service Share input',
		//	isAllowed: hasAccessToServiceShareInputSection,
		//	links: [],
		//	link: Routes.serviceShareInput.getPath()
		//},
		{
			name: 'Period status',
			isAllowed: hasAccessToPeriodStatusSection,
			links: [],
			link: Routes.periodStatus.getPath()
		},
		{
			name: 'Administrator',
			isAllowed: hasAccessToAdminSection,
			links: [
				{ header: 'Divisions', link: Routes.divisions.getPath() },
				{ header: 'Brands', link: Routes.brands.getPath() },
				{
					header: 'Leading Brands',
					link: Routes.leadingBrands.getPath()
				},
				{ header: 'Countries', link: Routes.countries.getPath() },
				{ header: 'Users', link: Routes.users.getPath() },
				{
					header: 'User Access rights',
					link: Routes.userAccessRights.getPath()
				},
				{
					header: 'Product Details',
					link: Routes.productDetails.getPath()
				},
				{
					header: 'Welcome admin',
					link: Routes.welcomeAdmin.getPath()
				},
				{
					header: 'Manage reminders',
					link: Routes.manageReminders.getPath()
				},
				{
					header: 'Notification logs',
					link: Routes.notificationlogs.getPath()
				}
			],
			link: Routes.welcomeAdmin.getPath()
		}
	];

	const getLinkClassName = (linkGroup: ILinkGroup): string => {
		if (linkGroup.link === location.pathname) {
			return `${styles.link} ${styles.active}`;
		}

		if (
			linkGroup.name === 'Administrator' &&
			location.pathname.includes('administrator')
		) {
			return `${styles.link} ${styles.active}`;
		}

		return styles.link;
	};

	const getChildLinkClassName = (childLink: ILink): string => {
		if (childLink.link === location.pathname) {
			return `${styles.link} ${styles.active}`;
		}

		return styles.link;
	};

	const environmentWarning = () => {
		const message = currentEnvironment === 'stg' ? 'Staging' : 'Dev';
		return (
			<div
				style={{
					display: 'flex',
					backgroundColor: 'red',
					justifyContent: 'center',
					alignItems: 'center',
					height: '2em'
				}}
			>
				<h3 style={{ color: 'white' }}>Environment - {message}</h3>
			</div>
		);
	};

	return (
		<nav className={styles.nav}>
			{showEnvironmentWarning && environmentWarning()}
			<div className={styles.whiteHeader}>
				<Link to={Routes.home.getPath()}>
					<img
						src='/assets/ACLogo.svg'
						alt='AC Logo'
						className={styles.logo}
					/>
				</Link>

				<p className={styles.logoLabel}>Market Share</p>

				{linkGroups
					.filter((link) => link.isAllowed)
					.map((link) => {
						return (
							<Link
								key={link.name}
								to={link.link!}
								title={link.name}
								className={getLinkClassName(link)}
								onClick={() => setActiveGroup(link.name)}
							>
								{link.name}
							</Link>
						);
					})}

				<span className={styles.username}>
					<strong>Welcome,</strong> {user.firstname} {user.lastname}
				</span>
			</div>
			{activeGroup === 'Administrator' ||
			location.pathname.includes('administrator') ? (
				<div className={styles.blueHeader}>
					{linkGroups.map((linkGroup) =>
						linkGroup.links.map((link) => (
							<Link
								key={link.header}
								to={link.link!}
								title={link.header}
								className={getChildLinkClassName(link)}
								onClick={() => setActiveChildLink(link.header)}
							>
								{link.header}
							</Link>
						))
					)}
				</div>
			) : undefined}
		</nav>
	);
};

export default Header;
